import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to track scroll direction and determine if a header should be visible.
 * Supports manual toggling via custom events to sync multiple headers.
 * Includes a cooldown after manual show to prevent immediate scroll-hiding.
 */
export const useScrollHeader = (options = {}) => {
  const { threshold = 50, scrollContainerId } = options;
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const manualCooldownRef = useRef(false);

  const toggleVisibility = useCallback((isVisible) => {
    setVisible(isVisible);
    if (isVisible) {
      // If manually showing, prevent scroll-hide for 2 seconds
      manualCooldownRef.current = true;
      setTimeout(() => { manualCooldownRef.current = false; }, 2000);
    }
    // Dispatch event to sync other headers
    const event = new CustomEvent('sync-header-visibility', { detail: { visible: isVisible } });
    window.dispatchEvent(event);
  }, []);

  useEffect(() => {
    const handleSync = (e) => {
      if (e.detail.visible !== visible) {
        setVisible(e.detail.visible);
      }
    };

    window.addEventListener('sync-header-visibility', handleSync);

    const handleScroll = () => {
      if (manualCooldownRef.current) return;

      const container = scrollContainerId 
        ? document.getElementById(scrollContainerId) 
        : (document.querySelector('.app-main') || window);
      
      const currentScrollY = scrollContainerId 
        ? (container?.scrollTop || 0) 
        : (window.pageYOffset || document.documentElement.scrollTop);

      if (currentScrollY > threshold) {
        if (currentScrollY > lastScrollY) {
          if (visible) setVisible(false);
        } else if (currentScrollY < lastScrollY - 20) {
          // Scrolling UP - reveal
          if (!visible) setVisible(true);
        }
      } else {
        // Near top - always show
        if (!visible) setVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    const container = scrollContainerId 
      ? document.getElementById(scrollContainerId) 
      : (document.querySelector('.app-main') || window);

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      if (container === window) {
          window.addEventListener('scroll', handleScroll, { passive: true });
      }
    }

    return () => {
      window.removeEventListener('sync-header-visibility', handleSync);
      if (container) {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [lastScrollY, threshold, scrollContainerId, visible]);

  return [visible, toggleVisibility];
};
